import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MainNavMenuComponent } from '../main-nav-menu/main-nav-menu.component';
import { ProfileNavMenuComponent } from '../profile-nav-menu/profile-nav-menu.component';
import { SecondNavMenuComponent } from '../second-nav-menu/second-nav-menu.component';
import { AccountService } from "../../../core-module/services/account.service";
import { takeUntil, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ObservableBaseComponent } from "../../../shared-module/components/observable-base.component";
import { RoleService } from "../../../core-module/services/role.service";
import { Permissions } from '../../../core-module/constants/permission.constants';
import { toSignal } from '@angular/core/rxjs-interop';

interface SecondMenuItem {
  id: number;
  name: string;
  imgUrl: string;
  externalLink?: string;
  navCommands: string[];
  permissions: string[];
  order: number;
}

@Component({
    selector: 'app-internal-nav-menu',
    imports: [
    RouterModule,
    MainNavMenuComponent,
    ProfileNavMenuComponent,
    SecondNavMenuComponent
],
    templateUrl: './internal-nav-menu.component.html',
    styleUrl: './internal-nav-menu.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InternalNavMenuComponent extends ObservableBaseComponent {

  private accountService = inject(AccountService);

  isExpanded = false;

  collapse() {
    this.isExpanded = false;
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  menu = toSignal(this.accountService.currentUser$
    .pipe(
      takeUntil(this.unsubscribe$),
      switchMap(usr => {
        if (!usr) {
          return [];
        };
        const roleNames = usr.roles ? usr.roles.map(r => r.name!) : [];
        return of({
          mainMenuItems: this.getMainMenuItems(roleNames, usr.permissions!),
          // managementMenuItems: this.getManagementItems(roleNames, usr.permissions!),
          secondMenuItems: this.getSecondMenuItems(roleNames, usr.permissions!)
        });
      })
    ));

  private getMainMenuItems(userRoles: string[], userPermissions: string[]) {
    let items = [
      {
        id: 1,
        name: 'Dashboard',
        navCommands: ['/dashboard'],
        expanded: false,
        subItems: [],
        order: 1,
        roles: [],
      },
      {
        id: 2,
        name: 'Pipeline',
        expanded: false,
        subItems: this.getPipelineSubItems(userRoles, userPermissions),
        roles: [],
        order: 2
      },
      {
        id: 3,
        name: 'Education',
        expanded: false,
        subItems: this.getEducationSubItems(),
        roles: [],
        order: 3
      },
      // {
      //   id: 4,
      //   name: 'Marketing Hub',
      //   expanded: false,
      //   subItems: this.getMarketingHubSubItems(userRoles),
      //   roles: [],
      //   order: 4
      // },
      {
        id: 5,
        name: 'Calculators',
        navCommands: ['/cal'],
        expanded: false,
        subItems: [],
        roles: [],
        order: 5
      }
    ];

    //items = items.filter(x => userRoles.some(r => x.roles.includes(r)) || x.roles.length == 0);

    return items;
  }

  private getPipelineSubItems(userRoles: string[], userPermissions: string[]) {
    let items = [
      {
        name: 'Loans Closed',
        imgSrc: '/assets/images/marketing-flyers.png',
        description: 'See your closed loan pipeline, updated daily.',
        navCommands: ['/loans'],
        permissions: [
          Permissions.Pipeline.Loan.FullControl
        ],
        col: 1
      },
      {
        name: 'Reports',
        imgSrc: '/assets/images/report.png',
        description: 'View and download your closed loan data.',
        navCommands: ['/reports'],
        col: 2
      },
      {
        name: 'Goals',
        imgSrc: '/assets/images/goals.png',
        description: 'Keep track of your sales goals.',
        navCommands: ['/loans/goals'],
        col: 2
      },
      {
        name: 'Company Rankings',
        imgSrc: '/assets/images/company-ranking.png',
        description: 'View company rankings for each department. Funders, Processors, and Loan Officers.',
        navCommands: ['/loans/company-rankings'],
        col: 3
      },
      {
        name: 'Payrolls',
        imgSrc: '/assets/images/payroll.png',
        description: 'View and manage payroll information for all departments.',
        navCommands: ['/payrolls'],
        col: 1
      }
    ];

    // Helper method to update navigation commands based on roles
    // const updateNavCommands = (itemName: string, navCommandsGetter: (name: string) => any[]) => {
    //   const filteredCommands = navCommandsGetter(itemName)
    //     .filter(x => {
    //       return userRoles.some(r => x.roles && x.roles.includes(r))
    //         || userPermissions.some(p => x.permissions && x.permissions.includes(p))
    //     })
    //     .map(m => {
    //       return m;
    //     });

    //   if (filteredCommands && filteredCommands.length > 0) {
    //     // Update the item with the first matching command
    //     const item = items.find(f => f.name === filteredCommands[0].name);
    //     if (item) {
    //       item.navCommands = filteredCommands[0].navCommands;
    //     }
    //   } else {
    //     // Remove the item if no roles match
    //     items.splice(items.findIndex(f => f.name === itemName), 1);
    //   }
    // };

    // // Update navigation commands for each item
    // updateNavCommands('Loans Closed', this.getLoansClosedNavCommands.bind(this));
    // updateNavCommands('Reports', this.getReportNavCommands.bind(this));
    // updateNavCommands('Goals', this.getGoalsNavCommands.bind(this));
    // updateNavCommands('Company Rankings', this.getCompanyRankingNavCommand.bind(this));
    
    this.updateMenuItems('Loans Closed', userRoles, userPermissions, items, this.getLoansClosedNavCommands('Loans Closed'));
    this.updateMenuItems('Goals', userRoles, userPermissions, items, this.getGoalsNavCommands('Goals'));
    this.updateMenuItems('Company Rankings', userRoles, userPermissions, items, this.getCompanyRankingNavCommand('Company Rankings'));

    return items;
  }

  private getLoansClosedNavCommands(name: string) {
    return [
      {
        name: name,
        navCommands: ['/loans'],
        permissions: [
          Permissions.Pipeline.Loan.FullControl,
          Permissions.Pipeline.Loan.ReadAll
        ]
      },
      {
        name: name,
        navCommands: ['/branch-loans'],
        permissions: [Permissions.Pipeline.Loan.ReadBranch]
      },
      {
        name: name,
        navCommands: ['/my-loans'],
        permissions: [Permissions.Pipeline.Loan.Read]
      }
    ];
  }

  private getGoalsNavCommands(name: string) {
    return [
      {
        name: name,
        navCommands: ['/loans/goals'],
        permissions: [Permissions.Pipeline.Loan.FullControl]
      },
      {
        name: name,
        navCommands: ['/branch-loans/goals/branches'],
        permissions: [Permissions.Pipeline.Loan.ReadBranch]
      },
      {
        name: name,
        navCommands: ['/my-loans/goals'],
        permissions: [Permissions.Pipeline.Loan.Read]
      }
    ];
  }

  private getCompanyRankingNavCommand(name: string) {
    return [
      {
        name: name,
        navCommands: ['/loans/company-rankings'],
        permissions: [Permissions.Pipeline.Loan.FullControl]
      },
      {
        name: name,
        navCommands: ['/branch-loans/company-rankings'],
        permissions: [Permissions.Pipeline.Loan.ReadBranch]
      },
      {
        name: name,
        navCommands: ['/my-loans/company-rankings'],
        permissions: [Permissions.Pipeline.Ranking.Read]
      }
    ];
  }

  private getEducationSubItems() {
    let items = [
      {
        name: 'Training Videos',
        imgSrc: '/assets/images/training-videos.png',
        description: 'Watch training videos and learn how to utilize all systems and softwares.',
        navCommands: ['/training/videos'],
        col: 1
      },
      {
        name: 'Training Portal',
        imgSrc: '/assets/images/lo-training.png',
        description: 'Your hub for mortgage learning and growth',
        navCommands: ['/training-portal'],
        col: 2
      },
      {
        name: 'Compliance Center',
        imgSrc: '/assets/images/compliance-center.png',
        description: 'Compliance Center. Yours trusted resource for staying informed, aligned, and compliant every step of the way.',
        navCommands: ['/compliance'],
        // externalLink: 'https://sclcompliancetraining.replit.app',        
        col: 3
      }
    ];

    return items;
  }

  private getMarketingHubSubItems(roles: string[]) {
    let items = [
      {
        name: 'Flyers',
        imgSrc: '/assets/images/marketing-flyers.png',
        description: 'Financial, Open House, and Loan Prepping Flyers.',
        navCommands: ['/marketing/flyers'],
        col: 1
      },
      {
        name: 'Graphics',
        imgSrc: '/assets/images/marketing-graphics.png',
        description: 'Ready to edit, or click download, and post social media graphics',
        navCommands: ['/marketing/graphics'],
        col: 1
      },
      {
        name: 'Marketing Education',
        imgSrc: '/assets/images/marketing-edu.png',
        description: 'Take a deep dive into what the Secure Marketing Hub has for you. (COMING SOON...)',
        navCommands: [],
        col: 2
      },
      {
        name: 'CRM',
        imgSrc: '/assets/images/crm.png',
        description: 'Utilize SCL post-closing CRM to stay top of mind.',
        navCommands: ['/crm/campaigns'],
        col: 3
      }
    ];

    //items = items.filter(x => roles.some(r => x.roles.includes(r)) || x.roles.length == 0);

    return items;
  }

  private getSecondMenuItems(userRoles: string[], userPermissions: string[]) {
    const items: SecondMenuItem[] = [
      {
        id: 1,
        name: 'Today\'s Market Data',
        imgUrl: '/assets/images/investors.png',
        externalLink: undefined,
        navCommands: ['/today-market-data'],
        permissions: [Permissions.Other.EmployeeDirectory.Read],
        order: 1
      },
      {
        id: 2,
        name: 'employee directory',
        navCommands: ['/employees'],
        imgUrl: '/assets/images/employee-directory.png',
        externalLink: undefined,
        permissions: [Permissions.Other.EmployeeDirectory.Read],
        order: 2
      },
      {
        id: 3,
        name: 'investors',
        navCommands: ['/investors'],
        imgUrl: '/assets/images/investors.png',
        externalLink: undefined,
        permissions: [Permissions.Other.Investor.Read],
        order: 3
      }
    ];

    return items.filter(x => userPermissions.some(p => x.permissions && x.permissions.includes(p)));
  }

  private updateMenuItems(itemName: string, userRoles: string[], userPermissions: string[], defaultItems: any[], updateNavItems: any[]) {
    const filteredCommands = updateNavItems
        .filter(x => {
          return userRoles.some(r => x.roles && x.roles.includes(r))
            || userPermissions.some(p => x.permissions && x.permissions.includes(p))
        })
        .map(m => {
          return m;
        });

      if (filteredCommands && filteredCommands.length > 0) {
        // Update the item with the first matching command
        const item = defaultItems.find(f => f.name === filteredCommands[0].name);
        if (item) {
          item.navCommands = filteredCommands[0].navCommands;
        }
      }
      else {
        // Remove the item if no roles match
        defaultItems.splice(defaultItems.findIndex(f => f.name === itemName), 1);
      }
  }

}
